import { faCaretDown, faSignOutAlt } from '@fortawesome/free-solid-svg-icons'
import { Box, Typography } from '@mui/material'
import { useKeycloak } from '@react-keycloak/web'

import { DropdownMenu } from '@/components'
import { useAppContext, useAppDispatch, useAppStore } from '@/hooks'
import { logout } from '@/store/modules/auth/actions'

import {
	UserAvatar,
	UserAvatarLarge,
	UserDropIcon,
	UserMenuButton,
	UserMenuLogout,
	UserMenuPanel,
	UserName,
	UserNamePrimary,
	UserNameSecondary,
} from './styles'

const getInitials = (name?: string, surname?: string) => {
	const first = name?.trim()?.[0] ?? ''
	const last = surname?.trim()?.[0] ?? ''
	const initials = `${first}${last}`.toUpperCase()
	return initials || '?'
}

export const UserMenu = ({ className = '' }: { className?: string }) => {
	const dispatch = useAppDispatch()
	const { keycloak } = useKeycloak()
	const { t } = useAppContext()

	const user = useAppStore((store) => store.auth.user)
	const userName = `${user?.name ?? ''} ${user?.surname ?? ''}`.trim()
	const tenantLabel = user?.companyName || keycloak.realm || ''
	const initials = getInitials(user?.name, user?.surname)

	const handleLogoutClick = () => {
		dispatch(logout())
	}

	if (!keycloak.authenticated) {
		return null
	}

	const trigger = (isOpen: boolean) => (
		<UserMenuButton
			className={className}
			$isOpen={isOpen}
			aria-haspopup="true"
			aria-expanded={isOpen}
			data-testid="navbar-user-menu"
		>
			<UserAvatar aria-hidden>{initials}</UserAvatar>
			<UserName>
				<UserNamePrimary>{userName || t('USER')}</UserNamePrimary>
				{tenantLabel ? (
					<UserNameSecondary data-testid="navbar-company">
						{tenantLabel}
					</UserNameSecondary>
				) : null}
			</UserName>
			<UserDropIcon icon={faCaretDown} />
		</UserMenuButton>
	)

	return (
		<DropdownMenu
			trigger={trigger}
			contentStyle={{
				padding: 0,
				width: 240,
				border: 'none',
				borderRadius: 10,
				boxShadow: '0 8px 24px rgba(26, 46, 53, 0.18)',
				overflow: 'hidden',
			}}
		>
			<UserMenuPanel role="menu">
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1.25,
						px: 1.75,
						py: 1.5,
					}}
				>
					<UserAvatarLarge aria-hidden>{initials}</UserAvatarLarge>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography
							sx={{
								fontSize: 14,
								fontWeight: 600,
								color: '#1F4954',
								lineHeight: 1.3,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{userName || t('USER')}
						</Typography>
						{tenantLabel ? (
							<Typography
								sx={{
									fontSize: 12,
									color: '#7A7A7A',
									lineHeight: 1.35,
									mt: '2px',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{tenantLabel}
							</Typography>
						) : null}
					</Box>
				</Box>
				<Box sx={{ height: 1, bgcolor: '#ECECEC' }} />
				<UserMenuLogout
					onClick={handleLogoutClick}
					role="menuitem"
					data-testid="navbar-logout"
				>
					<UserDropIcon icon={faSignOutAlt} />
					{t('LOGOUT')}
				</UserMenuLogout>
			</UserMenuPanel>
		</DropdownMenu>
	)
}
